import { create } from 'zustand';

export type ThemeName = 'light' | 'dark';

/** What the account stores: a fixed theme, or follow the device. */
export type ThemePreference = ThemeName | 'system';

const STORAGE_KEY = 'workinance-theme';

interface IThemeStore {
	theme: ThemeName;
	setTheme: (theme: ThemeName) => void;
}

function readStoredTheme(): ThemeName {
	try {
		return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
	} catch {
		// Storage can be blocked (private mode); the theme then simply resets to light on reload.
		return 'light';
	}
}

function applyTheme(theme: ThemeName) {
	document.documentElement.dataset.theme = theme;
	try {
		localStorage.setItem(STORAGE_KEY, theme);
	} catch {
		// Not being able to remember the choice must not stop it applying now.
	}
}

/** The signed-in app's light or dark theme. Remembered on this device and applied through data-theme on the root element. */
export const useThemeStore = create<IThemeStore>((set) => ({
	theme: 'light',
	setTheme: (theme) => {
		applyTheme(theme);
		set({ theme });
	},
}));

/** Call once before the app renders, so the saved theme is on screen from the first paint. */
export function initTheme() {
	const theme = readStoredTheme();
	document.documentElement.dataset.theme = theme;
	useThemeStore.setState({ theme });
}

/** Applies the theme saved on the user's account, once it is known. Nothing happens when the account has no preference yet. */
export function applyPreferredTheme(
	preferredTheme: ThemePreference | null | undefined,
) {
	if (!preferredTheme) return;

	const theme: ThemeName =
		preferredTheme === 'system'
			? window.matchMedia('(prefers-color-scheme: dark)').matches
				? 'dark'
				: 'light'
			: preferredTheme;
	applyTheme(theme);
	useThemeStore.setState({ theme });
}
